import type { BlobUploadProgress } from '../../api/blobUpload';
import type { SedrFile } from '../../api/sedr';
import { fileTypeIconFor, fileTypeLabelKeyFor } from '../../files/fileTypes';
import { applyI18n, t } from '../../i18n';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { bindFilesDropUpload } from '../../ui/filesDropUpload';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { fileTypeImg } from '../../ui/fileTypeImg';
import { renderBreadcrumbNav } from '../../ui/filesBreadcrumb';
import {
  bindFilesBrowseMobileTree,
  type FilesBrowseMobileTreeHandle,
} from '../../ui/filesBrowseMobile';
import {
  applyDirTreeNodeContent,
  collectSedrNameMatches,
  expandFoldersForSearchHits,
  fillHighlightedText,
  mountFilesTreeSearchBar,
  sedrAncestorKeys,
  stepSearchIndex,
  toSearchState,
  type FilesTreeSearchBarHandle,
  type FilesTreeSearchMatch,
} from '../../ui/filesTreeSearch';
import { applyUploadProgress, hideUploadProgress } from '../../ui/uploadProgress';
import { sedrEvidenceUploadErrorMessageKey } from './sedrErrors';
import {
  buildSedrDirectoryTree,
  listSedrFolderContents,
  sedrFileParentPath,
  sedrFolderCrumbs,
  type SedrDirectoryTreeNode,
} from './sedrEvidenceExplorer';

export interface SedrFilePickerUploadResult {
  files: SedrFile[];
  uploadedFileIds: string[];
}

export interface SedrFilePickerOptions {
  files: SedrFile[];
  excludeFileIds?: Iterable<string>;
  formatSize: (bytes: number) => string;
  titleKey?: string;
  hintKey?: string;
  onUploadFiles?: (
    parentId: string | null,
    files: File[],
    onProgress: (progress: BlobUploadProgress) => void,
  ) => Promise<SedrFilePickerUploadResult>;
}

/**
 * Modal file picker with directory tree + folder listing.
 * Returns the selected file id, or null if cancelled.
 */
export async function showSedrFilePickerDialog(
  input: SedrFilePickerOptions,
): Promise<string | null> {
  const exclude = new Set(input.excludeFileIds ?? []);
  const allowUpload = typeof input.onUploadFiles === 'function';

  let workingFiles = input.files;
  let selectableIds = new Set(
    workingFiles
      .filter((f) => !f.isDirectory && Boolean(f.blobId) && !exclude.has(f.id))
      .map((f) => f.id),
  );
  let filesById = new Map(workingFiles.map((f) => [f.id, f]));
  let tree = buildSedrDirectoryTree(workingFiles);

  const rebuildFileIndexes = (nextFiles: SedrFile[]) => {
    workingFiles = nextFiles;
    selectableIds = new Set(
      workingFiles
        .filter((f) => !f.isDirectory && Boolean(f.blobId) && !exclude.has(f.id))
        .map((f) => f.id),
    );
    filesById = new Map(workingFiles.map((f) => [f.id, f]));
    tree = buildSedrDirectoryTree(workingFiles);
  };

  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: input.titleKey ?? 'sedr.picker.title',
      helpTopic: dialogHelpTopic('sedrFilePicker'),
      className: 'sedr-file-picker-dialog',
    });

    const uploadButtonHtml = allowUpload
      ? `<label class="button primary files-folder-panel__upload" id="sedr-file-picker-upload-label"><span data-i18n="files.owner.upload"></span><input type="file" id="sedr-file-picker-upload" multiple hidden /></label>`
      : '';

    shell.body.innerHTML = `
      <p class="muted" data-i18n="${input.hintKey ?? 'sedr.picker.hint'}"></p>
      <div class="sedr-file-picker">
        <div class="files-browse-tree-pane">
          <div id="sedr-file-picker-tree-search"></div>
          <nav class="files-dir-tree sedr-file-picker__tree" id="sedr-file-picker-tree" role="tree"></nav>
        </div>
        <section class="sedr-file-picker__files files-browse-files">
          <div class="files-explorer-bar">
            <nav class="files-folder-breadcrumb" id="sedr-file-picker-breadcrumb" aria-label="/"></nav>
            <div class="files-explorer-bar__actions">
              ${uploadButtonHtml}
            </div>
          </div>
          <div class="files-table-wrap sedr-file-picker__table-wrap">
            <table class="data-table files-table">
              <thead>
                <tr>
                  <th data-i18n="sedr.evidence.colName"></th>
                  <th data-i18n="sedr.evidence.colType"></th>
                  <th data-i18n="sedr.evidence.colSize"></th>
                </tr>
              </thead>
              <tbody id="sedr-file-picker-tbody"></tbody>
            </table>
            <p class="muted sedr-file-picker__empty" id="sedr-file-picker-empty" hidden data-i18n="sedr.evidence.empty"></p>
          </div>
          <div class="sedr-file-picker__selection" id="sedr-file-picker-selection" aria-live="polite"></div>
          <p class="status-text" id="sedr-file-picker-status" hidden></p>
          <progress class="files-upload-progress" id="sedr-file-picker-progress" hidden></progress>
          <p class="error-text" id="sedr-file-picker-error" hidden></p>
        </section>
      </div>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'sedr-file-picker-cancel';
    cancelButton.innerHTML = '<span data-i18n="common.cancel"></span>';

    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'button primary';
    confirmButton.id = 'sedr-file-picker-confirm';
    confirmButton.disabled = true;
    confirmButton.innerHTML = '<span data-i18n="sedr.picker.choose"></span>';

    shell.actions.append(cancelButton, confirmButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    let currentParentId: string | null = null;
    let selectedFileId: string | null = null;
    let searchQuery = '';
    let searchMatches: FilesTreeSearchMatch[] = [];
    let searchIndex = -1;
    let searchHitEntryId: string | null = null;
    let searchBar: FilesTreeSearchBarHandle | null = null;
    let uploadBusy = false;
    const expandedPaths = new Set<string | null>([null]);
    let disposeDrop: (() => void) | undefined;
    let mobileTree: FilesBrowseMobileTreeHandle | undefined;

    const treeHost = shell.body.querySelector('#sedr-file-picker-tree') as HTMLElement;
    const breadcrumbHost = shell.body.querySelector('#sedr-file-picker-breadcrumb') as HTMLElement;
    const tbody = shell.body.querySelector('#sedr-file-picker-tbody') as HTMLTableSectionElement;
    const emptyEl = shell.body.querySelector('#sedr-file-picker-empty') as HTMLParagraphElement;
    const selectionEl = shell.body.querySelector('#sedr-file-picker-selection') as HTMLElement;
    const statusEl = shell.body.querySelector('#sedr-file-picker-status') as HTMLParagraphElement;
    const progressEl = shell.body.querySelector('#sedr-file-picker-progress') as HTMLProgressElement;
    const errorEl = shell.body.querySelector('#sedr-file-picker-error') as HTMLParagraphElement;
    const uploadInput = shell.body.querySelector('#sedr-file-picker-upload') as HTMLInputElement | null;
    const uploadLabel = shell.body.querySelector('#sedr-file-picker-upload-label') as HTMLLabelElement | null;
    const pickerSplit = shell.body.querySelector('.sedr-file-picker') as HTMLElement;
    const explorerBar = shell.body.querySelector('.files-explorer-bar') as HTMLElement;

    mobileTree = bindFilesBrowseMobileTree({ root: pickerSplit, explorerBar });

    const finish = (value: string | null) => {
      disposeDrop?.();
      mobileTree?.dispose();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => {
        if (uploadBusy) return;
        finish(null);
      },
    });

    const setUploadBusy = (busy: boolean) => {
      uploadBusy = busy;
      if (uploadInput) uploadInput.disabled = busy;
      if (uploadLabel) uploadLabel.classList.toggle('is-disabled', busy);
      confirmButton.disabled = busy || !selectedFileId;
      cancelButton.disabled = busy;
    };

    const updateSelectionChrome = () => {
      confirmButton.disabled = uploadBusy || !selectedFileId;
      selectionEl.replaceChildren();

      if (!selectedFileId) {
        selectionEl.className = 'sedr-file-picker__selection sedr-file-picker__selection--empty';
        const label = document.createElement('span');
        label.className = 'muted';
        label.dataset.i18n = 'sedr.picker.noneSelected';
        label.textContent = t('sedr.picker.noneSelected');
        selectionEl.append(label);
        applyI18n(selectionEl);
        return;
      }

      const file = filesById.get(selectedFileId);
      selectionEl.className = 'sedr-file-picker__selection';
      const caption = document.createElement('span');
      caption.className = 'sedr-file-picker__selection-label';
      caption.dataset.i18n = 'sedr.picker.selected';
      caption.textContent = t('sedr.picker.selected');
      selectionEl.append(caption);

      const path = document.createElement('span');
      path.className = 'sedr-file-picker__selection-path';
      if (file) {
        const dir = document.createElement('span');
        dir.className = 'sedr-file-picker__selection-dir';
        dir.textContent = sedrFileParentPath(workingFiles, file);
        const name = document.createElement('span');
        name.className = 'sedr-file-picker__selection-name';
        name.append(
          fileTypeImg(fileTypeIconFor(file.name, file.contentType), 14),
          document.createTextNode(file.name),
        );
        path.append(dir, name);
      } else {
        path.textContent = selectedFileId;
      }
      selectionEl.append(path);
      applyI18n(selectionEl);

      for (const row of tbody.querySelectorAll('.sedr-file-picker__row')) {
        const id = row.getAttribute('data-file-id');
        row.classList.toggle('sedr-file-picker__row--selected', id === selectedFileId);
      }
    };

    const selectFile = (fileId: string) => {
      selectedFileId = fileId;
      updateSelectionChrome();
    };

    const ensureExpanded = (id: string | null) => {
      const byId = new Map<string, SedrFile>();
      for (const f of workingFiles) {
        if (f.isDirectory) byId.set(f.id, f);
      }
      let cur: string | null = id;
      while (cur !== null) {
        expandedPaths.add(cur);
        const dir = byId.get(cur);
        if (!dir) break;
        cur = dir.parentId ?? null;
      }
    };

    const navigateTo = (id: string | null) => {
      mobileTree?.closeTree();
      currentParentId = id;
      ensureExpanded(id);
      refreshListing();
    };

    const parentIdFromDropPath = (path: string): string | null => (path === '' ? null : path);

    const runUpload = async (parentId: string | null, selected: File[]) => {
      if (!input.onUploadFiles || selected.length === 0 || uploadBusy) return;
      errorEl.hidden = true;
      setUploadBusy(true);
      statusEl.hidden = false;
      statusEl.textContent = t('files.upload.uploading', { percent: 0 });
      try {
        const result = await input.onUploadFiles(parentId, selected, (progress) => {
          applyUploadProgress(statusEl, progressEl, progress);
        });
        hideUploadProgress(statusEl, progressEl);
        rebuildFileIndexes(result.files);
        currentParentId = parentId;
        ensureExpanded(parentId);
        const lastId = result.uploadedFileIds[result.uploadedFileIds.length - 1];
        if (lastId && selectableIds.has(lastId)) {
          selectedFileId = lastId;
        }
        refreshListing();
      } catch (cause) {
        console.error('SEDR picker upload failed', cause);
        hideUploadProgress(statusEl, progressEl);
        errorEl.hidden = false;
        errorEl.textContent = t(sedrEvidenceUploadErrorMessageKey(cause));
      } finally {
        setUploadBusy(false);
      }
    };

    const applySearchQuery = (query: string, resetIndex: boolean) => {
      searchQuery = query;
      searchMatches = collectSedrNameMatches(searchQuery, workingFiles);
      if (resetIndex) {
        searchIndex = -1;
        searchHitEntryId = null;
      } else if (searchHitEntryId) {
        searchIndex = searchMatches.findIndex((m) => m.entryKey === searchHitEntryId);
        if (searchIndex < 0) searchHitEntryId = null;
      }
      searchBar?.setResultCount(searchMatches.length);
      if (searchMatches.length > 0) {
        expandFoldersForSearchHits(
          searchMatches,
          (key) => expandedPaths.add(key === '' ? null : key),
          (key) => sedrAncestorKeys(workingFiles, key === '' ? null : key),
        );
      }
    };

    const stepSearch = (direction: -1 | 1) => {
      if (searchMatches.length === 0) {
        applySearchQuery(searchQuery, true);
        if (searchMatches.length === 0) return;
      }
      searchIndex = stepSearchIndex(searchIndex, direction, searchMatches.length);
      const match = searchMatches[searchIndex];
      if (!match) return;
      searchHitEntryId = match.entryKey;
      const parentId = match.parentKey === '' ? null : match.parentKey;
      currentParentId = parentId;
      ensureExpanded(parentId);
      refreshListing();
    };

    const renderTreeNode = (
      node: SedrDirectoryTreeNode,
      level: number,
      searchState: ReturnType<typeof toSearchState>,
    ): HTMLLIElement => {
      const li = document.createElement('li');
      li.className = 'files-dir-tree__item';

      const path = node.id ?? '';
      const hasChildren = node.children.length > 0;
      const expanded = expandedPaths.has(node.id);

      const row = document.createElement('div');
      row.className = 'files-dir-tree__row';
      row.dataset.treePath = path;

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'files-dir-tree__toggle';
      toggle.tabIndex = -1;
      toggle.textContent = hasChildren ? (expanded ? '▾' : '▸') : '';
      toggle.disabled = !hasChildren;
      toggle.setAttribute('aria-hidden', hasChildren ? 'false' : 'true');
      if (hasChildren) {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          if (expandedPaths.has(node.id)) expandedPaths.delete(node.id);
          else expandedPaths.add(node.id);
          refreshListing();
        });
      }

      const nameBtn = document.createElement('button');
      nameBtn.type = 'button';
      nameBtn.className = `files-dir-tree__node${currentParentId === node.id ? ' files-dir-tree__node--active' : ''}`;
      nameBtn.style.paddingLeft = `${Math.max(0, level - 1)}rem`;
      nameBtn.dataset.treePath = path;
      applyDirTreeNodeContent(nameBtn, {
        icon: fileTypeImg('folder', 16),
        name: node.id === null ? '/' : node.name,
        folderKey: node.id ?? '',
        search: searchState,
      });
      nameBtn.addEventListener('click', () => navigateTo(node.id));

      row.append(toggle, nameBtn);
      li.append(row);

      if (hasChildren && expanded) {
        const childList = document.createElement('ul');
        childList.className = 'files-dir-tree__list';
        for (const child of node.children) {
          childList.append(renderTreeNode(child, level + 1, searchState));
        }
        li.append(childList);
      }
      return li;
    };

    const renderTree = (searchState: ReturnType<typeof toSearchState>) => {
      treeHost.replaceChildren();
      const list = document.createElement('ul');
      list.className = 'files-dir-tree__list';
      list.append(renderTreeNode(tree, 1, searchState));
      treeHost.append(list);
    };

    const renderBreadcrumb = () => {
      const crumbs = sedrFolderCrumbs(workingFiles, currentParentId);
      renderBreadcrumbNav(breadcrumbHost, {
        onRoot: () => navigateTo(null),
        rootCurrent: currentParentId === null,
        segments: crumbs.map((crumb) => ({
          label: crumb.name,
          current: crumb.id === currentParentId,
          onNavigate: () => navigateTo(crumb.id),
        })),
      });
    };

    const refreshListing = () => {
      applySearchQuery(searchQuery, false);
      const searchState = toSearchState(searchQuery, searchMatches);
      const { directories, fileItems } = listSedrFolderContents(workingFiles, currentParentId);
      const visibleFiles = fileItems.filter((f) => selectableIds.has(f.id));

      renderTree(searchState);
      renderBreadcrumb();
      tbody.replaceChildren();

      for (const dir of directories) {
        const tr = document.createElement('tr');
        tr.className = 'sedr-file-picker__row sedr-file-picker__row--folder';
        if (searchHitEntryId === dir.id) tr.classList.add('files-table__row--search-hit');
        tr.dataset.entryId = dir.id;
        tr.tabIndex = 0;
        const openDir = () => navigateTo(dir.id);
        const nameCell = document.createElement('td');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'files-table__name-button';
        const dirLabel = document.createElement('span');
        dirLabel.className = 'files-table__name-label';
        fillHighlightedText(dirLabel, dir.name, searchQuery);
        btn.append(fileTypeImg('folder', 16), dirLabel);
        btn.addEventListener('click', openDir);
        btn.addEventListener('dblclick', openDir);
        nameCell.append(btn);
        tr.append(nameCell);

        const typeCell = document.createElement('td');
        typeCell.className = 'files-table__type';
        typeCell.textContent = t('files.owner.folderType');
        tr.append(typeCell);

        const sizeCell = document.createElement('td');
        sizeCell.textContent = '—';
        tr.append(sizeCell);

        tr.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          openDir();
        });
        tr.addEventListener('dblclick', openDir);
        tr.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            openDir();
          }
        });
        tbody.append(tr);
      }

      for (const file of visibleFiles) {
        const tr = document.createElement('tr');
        tr.className = 'sedr-file-picker__row';
        if (searchHitEntryId === file.id) tr.classList.add('files-table__row--search-hit');
        tr.dataset.fileId = file.id;
        tr.dataset.entryId = file.id;
        tr.tabIndex = 0;

        const nameCell = document.createElement('td');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'files-table__name-button';
        const fileLabel = document.createElement('span');
        fileLabel.className = 'files-table__name-label';
        fillHighlightedText(fileLabel, file.name, searchQuery);
        btn.append(
          fileTypeImg(fileTypeIconFor(file.name, file.contentType), 16),
          fileLabel,
        );
        const activate = () => selectFile(file.id);
        btn.addEventListener('click', activate);
        btn.addEventListener('dblclick', () => {
          if (uploadBusy) return;
          selectedFileId = file.id;
          finish(file.id);
        });
        nameCell.append(btn);
        tr.append(nameCell);

        const typeCell = document.createElement('td');
        typeCell.className = 'files-table__type';
        typeCell.textContent = t(fileTypeLabelKeyFor(file.name, file.contentType));
        tr.append(typeCell);

        const sizeCell = document.createElement('td');
        sizeCell.textContent = file.sizeBytes != null ? input.formatSize(file.sizeBytes) : '—';
        tr.append(sizeCell);

        tr.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          activate();
        });
        tr.addEventListener('dblclick', () => {
          if (uploadBusy) return;
          selectedFileId = file.id;
          finish(file.id);
        });
        tr.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (uploadBusy) return;
            selectedFileId = file.id;
            finish(file.id);
          }
        });
        tbody.append(tr);
      }

      emptyEl.hidden = directories.length > 0 || visibleFiles.length > 0;
    if (searchHitEntryId) {
      const hitRow = tbody.querySelector(`tr[data-entry-id="${CSS.escape(searchHitEntryId)}"]`);
      hitRow?.scrollIntoView?.({ block: 'nearest' });
    }
      const pickerTable = shell.body.querySelector('table.data-table') as HTMLTableElement | null;
      if (pickerTable) {
        enhanceDataTableCards(pickerTable);
      }
      applyI18n(shell.dialog);
      updateSelectionChrome();
    };

    const searchHost = shell.body.querySelector('#sedr-file-picker-tree-search') as HTMLElement;
    searchBar = mountFilesTreeSearchBar(searchHost, {
      placeholderKey: 'files.search.placeholder',
      initialQuery: searchQuery,
      onQueryChange: (query) => {
        applySearchQuery(query, true);
        refreshListing();
      },
      onStep: stepSearch,
    });

    if (uploadInput) {
      uploadInput.addEventListener('change', () => {
        const selected = Array.from(uploadInput.files ?? []);
        uploadInput.value = '';
        if (selected.length > 0) void runUpload(currentParentId, selected);
      });
    }

    if (allowUpload) {
      disposeDrop = bindFilesDropUpload({
        root: pickerSplit,
        getCurrentPath: () => currentParentId ?? '',
        canUpload: () => allowUpload,
        isBusy: () => uploadBusy,
        onDrop: (dropped, targetPath) => {
          void runUpload(parentIdFromDropPath(targetPath), dropped);
        },
        onRejectedEmpty: () => {
          errorEl.hidden = false;
          errorEl.textContent = t('files.drop.emptyRejected');
        },
      });
    }

    cancelButton.addEventListener('click', () => {
      if (uploadBusy) return;
      finish(null);
    });
    confirmButton.addEventListener('click', () => {
      if (uploadBusy || !selectedFileId) return;
      finish(selectedFileId);
    });

    refreshListing();
    searchBar.focus();
  });
}
